"use client";

import type { ReactNode } from "react";
import {
  Users,
  Stethoscope,
  CalendarClock,
  Building2,
  UserCog,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";

/**
 * BusinessSettingsScreen (docs/ux/screens/BusinessSettingsScreen.md), shared by
 * both frontends (R22 T14).
 *
 * The hub for everything a clinic configures rather than works in day to day.
 * The business navigation has three sections: Schedule (what is happening),
 * Dashboard (who its customers are), and Settings (this one). Team, Services,
 * availability and the public profile were previously competing for space at
 * the top level; they are all configuration, so they live here and the nav
 * stays legible on a phone.
 *
 * **A hub of links needs no data of its own**, so it renders whether or not the
 * database is reachable. That is worth preserving: this is the screen somebody
 * lands on when they are trying to fix something, and a settings page that
 * fails because a read model failed is the least useful moment to be unhelpful.
 */

export type SettingsLinkComponent = (props: {
  href: string;
  className?: string;
  children: ReactNode;
}) => ReactNode;

interface SettingsLink {
  href: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

const SECTIONS: { title: string; links: SettingsLink[] }[] = [
  {
    title: "Your business",
    links: [
      {
        href: "/business/services",
        label: "Services",
        description: "What customers can request, how long it takes, what it costs",
        icon: Stethoscope,
      },
      {
        href: "/business/practitioners",
        label: "Team",
        description: "The practitioners customers can book with",
        icon: Users,
      },
      {
        href: "/business/schedule",
        label: "Opening hours",
        description: "The recurring week your open times are built from",
        icon: CalendarClock,
      },
      {
        href: "/business/profile",
        label: "Public profile",
        description: "Name, logo, categories, and location as customers see them",
        icon: Building2,
      },
    ],
  },
  {
    title: "Your account",
    links: [
      {
        href: "/profile",
        label: "Account",
        description: "Your own name, contact details, and password",
        icon: UserCog,
      },
    ],
  },
];

export function BusinessSettings({
  Link,
  logout,
  deleteAccount,
}: {
  Link: SettingsLinkComponent;
  /** The log-out control, which each host owns. */
  logout: ReactNode;
  /**
   * Deleting the member account (H58), which each host owns too. Rendered
   * last, after log out: a destructive action among ordinary ones gets clicked
   * by somebody aiming at the row above it (H34).
   */
  deleteAccount?: ReactNode;
}) {
  return (
    <main className="relative min-h-screen grow overflow-hidden bg-page px-4 py-8 text-body sm:px-5">
      <div className="mesh opacity-60" aria-hidden />

      <div className="relative mx-auto w-full max-w-3xl">
        <h1 className="text-2xl font-extrabold text-ink sm:text-3xl">Settings</h1>
        <p className="mt-1 text-sm text-muted">
          Everything you configure, in one place
        </p>

        {SECTIONS.map((section) => (
          <section key={section.title} className="mt-8">
            <h2 className="mb-3 font-mono text-xs uppercase tracking-widest text-muted">
              {section.title}
            </h2>
            <ul className="overflow-hidden rounded-2xl border border-line bg-surface shadow-[var(--shadow-card)]">
              {section.links.map((link, i) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className={`flex items-center gap-4 px-4 py-4 transition-colors hover:bg-page sm:px-5 ${
                      i > 0 ? "border-t border-line" : ""
                    }`}
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent/12 text-accent">
                      <link.icon className="size-5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-ink">
                        {link.label}
                      </span>
                      <span className="mt-0.5 block text-sm text-muted">
                        {link.description}
                      </span>
                    </span>
                    <ChevronRight className="size-5 shrink-0 text-muted" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <div className="mt-8 flex justify-center">{logout}</div>
        {deleteAccount && (
          <div className="mt-8 rounded-2xl border border-line bg-surface p-5 shadow-[var(--shadow-card)] sm:p-6">
            {deleteAccount}
          </div>
        )}
      </div>
    </main>
  );
}
